// Google Analytics 4 (gtag.js) integration
//
// Loaded ONLY after analytics consent (see the consent gate in main.ts),
// same policy as Statsig/Amplitude — no Google request, cookie or storage
// exists before the user consents (or implied consent outside strict regions).
// Google Consent Mode v2 is signalled BEFORE any config so it governs every
// hit: analytics_storage is granted (we never load pre-consent), all ad
// signals are denied (no Google Ads product on this site).
//
// Funnel events arrive via the central track() pipeline (analytics.ts): this
// module registers a sink at init, which also replays events buffered while
// consent was pending. SPA page_views are emitted per screen from that sink
// (send_page_view: false avoids the automatic double count).
//
// ID policy (matches Amplitude): no email and no user_id are sent to GA —
// user_id is the Supabase UUID, owned server-side.
import { registerAnalyticsSink } from './analytics.js';
import { getGoogleTagConfig } from '../config/env';
import { getEnvironment } from '../utils/environment';
import { getExperimentProperties } from '../config/experiments';

declare global {
  interface Window {
    dataLayer: any[];
    gtag: (...args: any[]) => void;
  }
}

// GA4 protocol limits for custom events
const GA4_MAX_EVENT_NAME_LENGTH = 40;
const GA4_MAX_PARAMS = 25;
const GA4_MAX_PARAM_VALUE_LENGTH = 100;

// Names gtag treats as special fields, not plain event params: our internal
// `session_id`/`user_id` would hijack GA4's native sid/uid (verified: sid/uid
// showed our values in /g/collect). GA manages its own client/session ids, and
// per our ID policy no user_id is ever sent to GA.
const GA4_RESERVED_PARAMS = new Set(['user_id', 'session_id']);

// Events NOT forwarded to GA4. `session_start` is a RESERVED GA4 event name,
// auto-collected by gtag.js on the first hit of each session — sending our own
// would duplicate it and violate GA4's naming rules.
const GA4_SKIPPED_EVENTS = new Set(['session_start']);

let gtagInitialized = false;

/**
 * Initialize the Google tag. Call ONLY after analytics consent is granted.
 */
export function initGoogleTag(): void {
  if (gtagInitialized) return;

  const cfg = getGoogleTagConfig();
  if (!cfg?.measurementId) {
    console.warn('Google tag measurement ID not configured for this environment — skipping init');
    return;
  }

  try {
    window.dataLayer = window.dataLayer || [];
    // eslint-disable-next-line prefer-rest-params
    window.gtag = window.gtag || function gtag() { window.dataLayer.push(arguments); };

    // Consent Mode v2 — must be set before any config so it governs every hit.
    window.gtag('consent', 'default', {
      analytics_storage: 'granted', // this module only loads post-consent
      ad_storage: 'denied',
      ad_user_data: 'denied',
      ad_personalization: 'denied'
    });

    window.gtag('js', new Date());

    // Local A/B arms as user-scoped properties (e.g. ab_face_scan) — the same
    // values also ride on every event via the central enricher. Must be set
    // before config so the first hit carries them.
    const experimentProps = getExperimentProperties();
    if (Object.keys(experimentProps).length > 0) {
      window.gtag('set', 'user_properties', experimentProps);
    }

    window.gtag('config', cfg.measurementId, {
      // SPA with a screen flow — page_view is emitted per screen by the sink
      send_page_view: false,
      // Sandbox/dev hits surface in GA4 DebugView. NOTE: debug_mode alone does
      // NOT exclude them from standard reports — the GA4 property must have the
      // "Developer traffic" data filter activated in Admin for that.
      ...(getEnvironment() !== 'production' ? { debug_mode: true } : {})
    });

    // Inject gtag.js (after consent defaults are queued on the dataLayer)
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${cfg.measurementId}`;
    document.head.appendChild(script);

    gtagInitialized = true;

    // Start receiving events from the central track() — including the replay
    // of events buffered while consent was pending.
    registerAnalyticsSink('ga4', trackGoogleTag);

    console.log(`✅ Google tag initialized (${cfg.measurementId})`);
  } catch (error) {
    console.error('Failed to initialize Google tag:', error);
  }
}

/**
 * Forward a tracked event to GA4.
 * - screen_view → GA4 page_view (SPA screen navigation)
 * - purchase   → GA4 purchase (no server-side GA exists, so no double count)
 * - everything else → sanitized custom event
 */
function trackGoogleTag(eventName: string, properties: Record<string, any> = {}): void {
  if (!gtagInitialized) return;
  if (GA4_SKIPPED_EVENTS.has(eventName)) return;

  if (eventName === 'screen_view') {
    const screenName = String(properties.screen_name || '');
    window.gtag('event', 'page_view', {
      page_title: screenName,
      // Screen flow has no per-screen URL — use the analytics screen name as a
      // stable virtual path (replay-safe: never reads the current location hash).
      page_path: `/#${screenName}`,
      page_location: window.location.origin + `/#${screenName}`
    });
    return;
  }

  if (eventName === 'purchase') {
    window.gtag('event', 'purchase', {
      transaction_id: String(properties.transaction_id || properties.transactionId || ''),
      value: Number(properties.value ?? properties.amount ?? 0),
      currency: String(properties.currency || 'USD')
    });
    return;
  }

  window.gtag('event', sanitizeEventName(eventName), sanitizeParams(properties));
}

// GA4 event names: letters/digits/underscores, must start with a letter, ≤40 chars
function sanitizeEventName(name: string): string {
  const cleaned = String(name).replace(/[^a-zA-Z0-9_]/g, '_').replace(/^[^a-zA-Z]+/, '');
  return (cleaned || 'unnamed_event').slice(0, GA4_MAX_EVENT_NAME_LENGTH);
}

// GA4 params: ≤25 per event, primitive values, string values ≤100 chars
function sanitizeParams(properties: Record<string, any>): Record<string, string | number | boolean> {
  const params: Record<string, string | number | boolean> = {};
  let count = 0;
  for (const [key, value] of Object.entries(properties)) {
    if (count >= GA4_MAX_PARAMS) break;
    if (value == null || GA4_RESERVED_PARAMS.has(key)) continue;
    const paramKey = sanitizeEventName(key); // same charset/length rules as names
    if (typeof value === 'number' || typeof value === 'boolean') {
      params[paramKey] = value;
    } else if (typeof value === 'string') {
      params[paramKey] = value.slice(0, GA4_MAX_PARAM_VALUE_LENGTH);
    } else {
      continue; // drop objects/arrays — GA4 params must be primitives
    }
    count++;
  }
  return params;
}
